import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

let gkSeq = 0;
/** @param {HTMLImageElement} src */
function gkDecode(src) {
  const img = new Image();
  const sizes = src.getAttribute('sizes');
  const srcset = src.getAttribute('srcset');
  if (sizes) img.sizes = sizes;
  if (srcset) img.srcset = srcset;
  img.src = src.getAttribute('src') || '';
  return img.decode().catch(() => {});
}

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        // Grinkly 23/10: cùng bộ ảnh (vd đổi size) → giữ nguyên, không vẽ lại / không nháy
        const ids = (/** @type {Element} */ g) =>
          Array.from(g.querySelectorAll('slideshow-slides [data-media-id]'))
            .map((m) => /** @type {HTMLElement} */ (m).dataset.mediaId)
            .join(',');
        const seq = ++gkSeq;
        if (ids(newMediaGallery) === ids(this)) return;

        // Khác bộ ảnh (đổi màu) → tải + giải mã ảnh đầu trước rồi mới thay, để không hiện khung trống
        const first = newMediaGallery.querySelector('slideshow-slide img');
        const ready = first ? gkDecode(/** @type {HTMLImageElement} */ (first)) : Promise.resolve();
        Promise.race([ready, new Promise((r) => setTimeout(r, 900))]).then(() => {
          if (seq !== gkSeq || !this.isConnected) return;
          this.replaceWith(newMediaGallery);
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
